/* eslint-disable react/prop-types */
import React, { useRef, useEffect, useState } from 'react'
import _ from 'lodash'
import { Modal } from 'react-bootstrap'
import CodeMirror from 'codemirror'
import styled from 'styled-components'

import i18n from 'live/services/i18n'
import Select from 'live/components/forms/ui/select'
import { Index } from './collections-table'

import { isLiveCollection, isRegularCollectionName } from '../../../lib/utils'

require('codemirror/mode/javascript/javascript')

export type MappingCollectionType = {
    name: string
    type: string | null
}

type ModalCreateIndexProps = {
    collections: MappingCollectionType[]
    preSelectedCollections?: MappingCollectionType[]
    show: boolean
    saveIndex: (selectedCollections: string[], index: Index) => void
    onHide: () => void
}

enum IndexType {
    usual = 'usual',
    partial = 'partial',
    sparse = 'sparse'
}

const ModalCreateIndex: React.FC<ModalCreateIndexProps> = (
    { preSelectedCollections, saveIndex, collections, onHide, show },
    ...props
) => {
    const validationOfForm = useRef(false)
    const [selectedCollections, setSelectedCollections] = useState<MappingCollectionType[]>([])
    const [validationError, setValidationError] = useState(false)
    const [key, setKey] = useState('')
    const [jsonError, setJsonError] = useState('')
    const [sparse, setSparse] = useState(false)
    const [partialFilterExpression, setPartialFilterExpression] = useState('')
    const [partialFilterExpressionError, setPartialFilterExpressionError] = useState<string | boolean>(false)
    const [chosenIndexType, setChosenIndexType] = useState<IndexType>(IndexType.usual)
    const indexTypes: IndexType[] = [IndexType.usual, IndexType.partial, IndexType.sparse]

    useEffect(() => {
        if (preSelectedCollections && preSelectedCollections.length > 0) {
            setSelectedCollections(preSelectedCollections)
        }
    }, [preSelectedCollections])

    useEffect(() => {
        if (validationOfForm.current) {
            validateCollections()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCollections])

    const parseJson = (value: string): { value: Record<string, any>; jsonError: string | null } => {
        let jsonError = null

        try {
            value = JSON.parse(value)
        } catch (e) {
            try {
                // add quotes to JSON keys
                value = value.replace(/([A-Za-z][^:]*)(?=\s*:)/g, '"$1"')
                value = value.replace(/"\s*?([A-z]+)\s*?"\s*:/, '"$1":')

                value = JSON.parse(value)
            } catch (e) {
                jsonError = e.message
            }
        }

        return { value: (value as unknown) as Record<string, any>, jsonError }
    }

    const validateJson = (json: string): { value: Record<string, any>; jsonError: string } => {
        const { value, jsonError } = parseJson(json)

        setJsonError(jsonError)

        return { value, jsonError }
    }

    const changeKey = (key: string): void => {
        const { jsonError } = parseJson(key)
        setKey(key)
        setJsonError(jsonError)
    }

    const changePartialFilterExpression = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const json = event.target.value
        setPartialFilterExpression(json)
        setPartialFilterExpressionError(json.trim().length > 0 ? parseJson(json).jsonError : false)
    }

    const onChangeCollection = (value: string): void => {
        const selected = [...selectedCollections]

        const [collection] = collections.filter(collection => collection.name === value)

        if (collection === null || collection === undefined) return

        const { name, type } = collection
        selected.push({ name, type })

        selected.sort((prevCollection, nextCollection) =>
            prevCollection.name > nextCollection.name ? 1 : nextCollection.name > prevCollection.name ? -1 : 0
        )

        validationOfForm.current = true
        setSelectedCollections(selected)
    }

    const removeCollection = (id: number): void => {
        const selected = [...selectedCollections]
        selected.splice(id, 1)
        validationOfForm.current = true
        setSelectedCollections(selected)
    }

    const validateCollections = (): boolean => {
        const isEmpty = _.isEmpty(selectedCollections)
        setValidationError(isEmpty)

        return isEmpty
    }

    const createIndex = (): void => {
        const { value: keyParse } = parseJson(key)
        const { value: partialFilterExpressionParse } = parseJson(partialFilterExpression)

        const index: Index = {
            key: keyParse,
            name: '',
            sparse,
            partialFilterExpression: partialFilterExpressionParse || null
        }
        const collectionsName = selectedCollections.map(s => s.name)
        saveIndex(collectionsName, index)
    }

    const apply = (): void => {
        const valid = validateCollections()

        const { jsonError } = validateJson(key)

        if (!jsonError && !valid) {
            createIndex()
            onHide()
        } else {
            validationOfForm.current = true
        }
    }

    const handleIndexType = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setChosenIndexType(event.target.id as IndexType)
        setSparse(event.target.id === 'sparse')
    }

    const capitalize = (word: string): string => {
        return word[0].toUpperCase() + word.slice(1).toLowerCase()
    }

    const collectionsName = selectedCollections.map(c => c.name)

    const collectionOption = collections
        .filter(c => !collectionsName.includes(c.name))
        .map(c => ({ value: c.name, label: c.name }))
        .sort((a, b) => (a.value > b.value) - (a.value < b.value))

    return (
        <Modal
            {...props}
            backdrop="static"
            className="modal-create-index--mongodb modal-lg"
            animation={false}
            onHide={onHide}
            show={show}
        >
            <Modal.Header>
                <Modal.Title>{i18n('Create index')}</Modal.Title>
            </Modal.Header>
            <form className="form">
                <Modal.Body>
                    <div className="row">
                        <div className="col-md-6">
                            <div className={'form-group ' + (jsonError ? 'has-error' : 'success')}>
                                <label>{i18n('Index expression') + ' (JSON)'}</label>
                                <div className="esr-rule-reminder">
                                    <i className="fa fa-info-circle"></i>
                                    <span>
                                        {i18n('Remember to follow the')}{' '}
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href="https://www.mongodb.com/blog/post/performance-best-practices-indexing"
                                        >
                                            {i18n('ESR rule')}
                                        </a>{' '}
                                        {i18n('when creating indexes')}
                                    </span>
                                </div>
                                <CreateIndexCodeMirror value={key} onChange={changeKey} />
                            </div>
                            <span className="form-group has-error expression-info">
                                <span className="expression-hint">
                                    {'Ex: \u007b depth:1, timestamp: 1, whatever: "hashed" \u007d'}
                                </span>
                                <span className="help-block">{jsonError || null}</span>
                            </span>
                            <div>
                                <span>{i18n('Index Type')}:</span>
                                <IndexTypesDiv>
                                    {indexTypes.map(indexType => {
                                        return (
                                            <CentralizedTextDiv key={indexType}>
                                                <input
                                                    checked={chosenIndexType === indexType}
                                                    type="radio"
                                                    id={indexType}
                                                    name="index-type"
                                                    value={indexType}
                                                    onChange={handleIndexType}
                                                />
                                                <label htmlFor={indexType}>{i18n(capitalize(indexType))}</label>
                                            </CentralizedTextDiv>
                                        )
                                    })}
                                </IndexTypesDiv>
                            </div>
                            {chosenIndexType === IndexType.partial && (
                                <div
                                    className={'form-group ' + (partialFilterExpressionError ? 'has-error' : 'success')}
                                >
                                    <label>{i18n('Partial filter expression')}</label>
                                    <input
                                        type={'text'}
                                        onChange={changePartialFilterExpression}
                                        name={'partialFilterExpression'}
                                        value={partialFilterExpression}
                                    />
                                    <span className="help-block">{partialFilterExpressionError || null}</span>
                                </div>
                            )}
                            {chosenIndexType === IndexType.sparse && (
                                <CentralizedTextDiv>
                                    <span>{i18n("Doesn't index documents that don't contain the indexed fields")}</span>
                                </CentralizedTextDiv>
                            )}
                        </div>
                        <div className="col-md-6">
                            <label>{i18n('Add this index to the following collections')}:</label>
                            <div>
                                <ul className="table-list">
                                    {_.isEmpty(selectedCollections) ? (
                                        <li className={`text-center ${validationError ? 'has-error text-danger' : ''}`}>
                                            {i18n('You must choose at least one collection')}
                                        </li>
                                    ) : (
                                        _.map(selectedCollections, function (collection, index) {
                                            const { name } = collection
                                            const fullName = name.split('.')

                                            return (
                                                <LICreateIndexCollection key={index}>
                                                    <div>
                                                        {isLiveCollection(name) ? (
                                                            isRegularCollectionName(name) ? (
                                                                <>
                                                                    <span className="preffix">{fullName[0]}.</span>
                                                                    <span>{fullName[1]}</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span className="preffix">
                                                                        {fullName[0]}.{fullName[1]}.
                                                                    </span>
                                                                    <span>{fullName[2]}</span>
                                                                </>
                                                            )
                                                        ) : (
                                                            <span>{name}</span>
                                                        )}
                                                        <div className="eventType">{collection.type}</div>
                                                    </div>
                                                    <a
                                                        className="pull-right text-danger"
                                                        onClick={() => removeCollection(index)}
                                                    >
                                                        <i className="fa fa-times fa-fw" />
                                                    </a>
                                                </LICreateIndexCollection>
                                            )
                                        })
                                    )}
                                </ul>
                            </div>
                            <Select
                                options={collectionOption}
                                onChange={onChangeCollection}
                                placeholder={i18n('Choose collection')}
                            />
                        </div>
                    </div>
                </Modal.Body>
                <div className="hint">
                    <span>
                        {i18n(
                            'Hint: it could take some time to index all the data from selected collections. During this process, index size will increase quickly and system performance can be affected.'
                        )}
                    </span>
                </div>
                <Modal.Footer>
                    <input
                        disabled={!!validationError || !!partialFilterExpressionError || !!jsonError}
                        type="button"
                        className="btn btn-primary"
                        value={i18n('Create')}
                        onClick={apply}
                    />
                    <span>{i18n('or')}</span>
                    <a className="cancel" onClick={onHide}>
                        {i18n('cancel')}
                    </a>
                </Modal.Footer>
            </form>
        </Modal>
    )
}

interface CreateIndexCodeMirrorProps {
    value: string
    onChange: (value: string) => void
}

type CreateIndexCodeMirrorThis = {
    textarea: React.RefObject<HTMLTextAreaElement>
    editor: null | CodeMirror.EditorFromTextArea
    props: CreateIndexCodeMirrorProps
}
class CreateIndexCodeMirror extends React.PureComponent<CreateIndexCodeMirrorProps> {
    textarea = React.createRef()

    componentDidMount(): void {
        const self = (this as unknown) as CreateIndexCodeMirrorThis
        if (self.textarea.current) this.setCodeMirror()
    }

    setCodeMirror(): void {
        if (this.textarea.current) {
            const self = (this as unknown) as CreateIndexCodeMirrorThis
            const editor = CodeMirror.fromTextArea(self.textarea.current, {
                mode: 'application/json',
                autofocus: false
            })

            editor.on('change', () => self.props.onChange(editor.getValue()))
            self.editor = editor
        }
    }

    render(): JSX.Element {
        return <textarea name="key" ref={this.textarea} value={this.props.value} />
    }
}

export default ModalCreateIndex

const IndexTypesDiv = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: 20px;
`

const CentralizedTextDiv = styled.div`
    text-align: center;
`

const LICreateIndexCollection = styled.li`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;

    div {
        word-break: break-all;
        padding-right: 5px;
    }

    a {
        align-self: flex-start;
    }
`
